import { Maximize2Icon, Minimize2Icon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogPanel, DialogPopup } from "@/components/ui/dialog";
import { MenuItem } from "@/components/ui/menu";
import { NAMESPACES } from "@/i18n/constants";
import type { CLOCK_CADENCE } from "@/providers/wall-clock";
import { CrosshairBusProvider } from "./data-viewer-provider";
import { DownloadCsvButton, DownloadCsvMenuItem } from "./export/download-csv-button";
import { PanelStack } from "./panels/panel-stack";
import { PanelLayoutMenuItem, PanelLayoutToggle, type PanelLayoutToggleProps } from "./spec/panel-layout-toggle";
import { AutoRefreshTicker } from "./time-window/auto-refresh-ticker";
import { RefreshButton, RefreshMenuItem } from "./time-window/refresh-button";
import { WindowRangePicker } from "./time-window/window-range-picker";
import { type ToolbarAction, ViewerToolbar } from "./toolbar/viewer-toolbar";

const EXPANDED_PANEL_HEIGHT_PX = 400;

interface ViewerPanelProps {
    resizable?: boolean;
    controls?: ReactNode;
    panelLayout?: PanelLayoutToggleProps;
    showCsvDownload?: boolean;
    baseHeightPx?: number;
    showRefresh: boolean;
    isEmpty?: boolean;
    /** Expand or collapse, at the end of the toolbar. */
    sizeAction?: ToolbarAction;
}

function ViewerPane({
    resizable = true,
    controls,
    panelLayout,
    showCsvDownload,
    baseHeightPx,
    showRefresh,
    isEmpty,
    sizeAction,
}: ViewerPanelProps) {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "messages" });

    const actions: ToolbarAction[] = [];
    if (panelLayout) {
        actions.push({
            key: "layout",
            priority: 2,
            button: <PanelLayoutToggle {...panelLayout} />,
            menuItem: <PanelLayoutMenuItem {...panelLayout} />,
        });
    }
    if (showCsvDownload) {
        actions.push({ key: "csv", priority: 1, button: <DownloadCsvButton />, menuItem: <DownloadCsvMenuItem /> });
    }
    if (showRefresh) {
        actions.push({
            key: "refresh",
            priority: 4,
            button: <RefreshButton />,
            menuItem: <RefreshMenuItem />,
        });
    }
    if (sizeAction) {
        actions.push(sizeAction);
    }

    return (
        <CrosshairBusProvider>
            {/* 4px steps here, not the app's 3.84px: the toolbar's height sets where the panels start, and a fractional start blurs their text at 1x. */}
            <div className={"flex flex-col gap-[16px] [--spacing:0.25rem]"}>
                <ViewerToolbar actions={actions} pinned={controls} range={<WindowRangePicker />} />
                {isEmpty ? (
                    <div className={"py-8 text-muted-foreground text-sm text-center"}>{t("empty")}</div>
                ) : (
                    <PanelStack resizable={resizable} baseHeightPx={baseHeightPx} />
                )}
            </div>
        </CrosshairBusProvider>
    );
}

interface DataViewerBodyProps {
    autoRefreshCadence?: CLOCK_CADENCE;
    resizable?: boolean;
    controls?: ReactNode;
    panelLayout?: PanelLayoutToggleProps;
    showCsvDownload?: boolean;
    baseHeightPx?: number;
    isEmpty?: boolean;
}

export function DataViewerBody({
    autoRefreshCadence,
    resizable,
    controls,
    panelLayout,
    showCsvDownload,
    baseHeightPx,
    isEmpty,
}: DataViewerBodyProps) {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "expand" });
    const [expanded, setExpanded] = useState(false);

    const showRefresh = autoRefreshCadence != null;

    return (
        <>
            {/* Both views share one store, so one ticker up here instead of a timer per view. */}
            {autoRefreshCadence != null && <AutoRefreshTicker cadence={autoRefreshCadence} />}
            <ViewerPane
                baseHeightPx={baseHeightPx}
                controls={controls}
                isEmpty={isEmpty}
                panelLayout={panelLayout}
                resizable={resizable}
                showCsvDownload={showCsvDownload}
                showRefresh={showRefresh}
                sizeAction={{
                    key: "expand",
                    priority: 5,
                    button: (
                        <Button
                            aria-label={t("open")}
                            onClick={() => setExpanded(true)}
                            size={"icon"}
                            tooltip={t("open")}
                            variant={"outline"}
                        >
                            <Maximize2Icon />
                        </Button>
                    ),
                    menuItem: (
                        <MenuItem onClick={() => setExpanded(true)}>
                            <Maximize2Icon />
                            {t("open")}
                        </MenuItem>
                    ),
                }}
            />
            <Dialog open={expanded} onOpenChange={setExpanded}>
                <DialogPopup
                    aria-label={t("title")}
                    className={"sm:max-w-[1200px] [--spacing:0.25rem]"}
                    presentation={"fullscreen"}
                    showCloseButton={false}
                >
                    {/* No DialogHeader here, so put back the top padding the panel drops when there is one. */}
                    <DialogPanel className={"pt-4"}>
                        <ViewerPane
                            baseHeightPx={EXPANDED_PANEL_HEIGHT_PX}
                            controls={controls}
                            isEmpty={isEmpty}
                            panelLayout={panelLayout}
                            resizable={resizable}
                            showCsvDownload={showCsvDownload}
                            showRefresh={showRefresh}
                            sizeAction={{
                                key: "collapse",
                                priority: 5,
                                button: (
                                    <DialogClose
                                        aria-label={t("close")}
                                        render={<Button size={"icon"} tooltip={t("close")} variant={"outline"} />}
                                    >
                                        <Minimize2Icon />
                                    </DialogClose>
                                ),
                                menuItem: (
                                    <MenuItem onClick={() => setExpanded(false)}>
                                        <Minimize2Icon />
                                        {t("close")}
                                    </MenuItem>
                                ),
                            }}
                        />
                    </DialogPanel>
                </DialogPopup>
            </Dialog>
        </>
    );
}
